import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Container, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, styled, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useMemo, useState } from "react";
import { Session } from "../../api/dtos";
import ExportButton from "../../components/common/ExportButton";
import useSessions from "../../hooks/useSessions";
import AdminNavigation from "./AdminNavigation";

const Anchor = styled('a')({})

const currentYear = new Date().getFullYear();

const renderYearSelectOptions = () => {
    let options: React.ReactElement[] = [];

    for(let i = 2022; i <= currentYear + 1;  i++){
        options.push(<MenuItem key={i} value={i.toString()}>{i}</MenuItem>);
    }

    return options;
}

const getDayString = (day: number) => {
    switch(day){
        case 0:
            return "Sun";
        case 1:
            return "Mon";
        case 2:
            return "Tue";
        case 3:
            return "Wed";
        case 4:
            return "Thu";
        case 5:
            return "Fri";
        case 6:
            return "Sat";
        default:
            return "";
    }
}

const getSessionName = (sessions: Session[], selectedSession: Session): string => {
    let name = selectedSession.name;

    if(sessions.filter(s => s.name == selectedSession.name)?.length > 1 && selectedSession?.startTime){
        name = `${name} (${getDayString(new Date(selectedSession.startTime).getDay())})`;
    }

    return name!;
}

const AdminReports: React.FunctionComponent = () => {

    const { sessions } = useSessions();

    const [session, setSession] = useState<number>(0);
    const [year, setYear] = useState<number>(0);

    const renderSessionSelectOptions = useMemo(() => {
        let options: React.ReactElement[] = [];

        sessions?.sort((a: any, b: any) => (a.rank ?? 0) - (b.rank ?? 0))?.map((session: any, index: any) => {
            options.push(<MenuItem key={`session-${session.id}`} value={session.id}>{getSessionName(sessions, session)}</MenuItem>);
        })

        return options;
    }, [sessions])

    const selectedSession = useMemo(() => {
        return sessions?.find((s: any) => s.id === session)
    }, [session]);

    return (
        <Box>
            <AdminNavigation/>
            <Box>
                <Container
                    maxWidth="lg"
                >
                    <Box
                        sx={{
                            px: {
                                xs: 1
                            },
                            mb: {
                                xs: 3
                            },
                            mt:{
                                xs: 2,
                                md: 5
                            }
                        }}
                    >
                        <Typography
                            variant="h2"
                            component="h1"
                            gutterBottom
                        >
                            Reports
                        </Typography>
                        <Grid
                            container
                            flexDirection="column"
                            spacing={3}
                            sx={{
                                mt: 5
                            }}
                        >
                            <Grid
                                item
                                xs={6}
                            >
                                <FormControl fullWidth size="small">
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                        sx={{
                                            width: '100%'
                                        }}
                                        variant="outlined"
                                        label="Year"
                                        value={year as any}
                                        onChange={(event: SelectChangeEvent) => setYear(+event.target.value)}
                                    >
                                        <MenuItem value={0}>All</MenuItem>
                                        {
                                            renderYearSelectOptions()
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/delegate/${year}`}>
                                    Badge Information
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/registration/${year}`}>
                                    Registration Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/outstandingPayment/${year}`}>
                                    Outstanding Payment Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/attendance/${year}`}>
                                    Attendance Statistics Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/additional-credit-card/${year}`}>
                                    Additional Credit Card Payments Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/attendee-payment/${year}`}>
                                    Attendee Payment Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/where-did-you-hear/${year}`}>
                                    Attendee Referred Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/cancellation-list/${year}`}>
                                    Attendee Cancellation Report
                                </ExportButton>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <ExportButton link={`/report/speaker/${year}`}>
                                    Speaker Presentations Report
                                </ExportButton>
                            </Grid>

                            {
                                sessions && (
                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Session Report</InputLabel>
                                            <Select
                                                sx={{
                                                    width: '100%'
                                                }}
                                                variant="outlined"
                                                label="Session Report"
                                                onChange={(event: SelectChangeEvent) => setSession(+event.target.value)}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <Tooltip
                                                            title="Must select a session"
                                                            disableHoverListener={!!selectedSession}
                                                        >
                                                            <span>
                                                                <Button
                                                                    href={`/report/session/${year}/${session}`}
                                                                    variant="text"
                                                                    disabled={!selectedSession}
                                                                    sx={{ minWidth: 0, marginRight: '-12px' }}
                                                                >
                                                                    <FontAwesomeIcon icon={faFileExcel} fixedWidth size="2x" />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                }
                                            >
                                                {
                                                    renderSessionSelectOptions
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )
                            }
                        </Grid>
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

export default AdminReports
